/**
 * PL-Line chrome scroll coupling.
 *
 * Port of the Next.js storefront's scroll listener (`nav-client.tsx` →
 * `html.pl-chrome-hidden`): scrolling down slides the green announcement bar out of
 * view and drops the header pill to the top of the viewport; scrolling up — or
 * sitting near the top of the page — brings both back. The slide itself is CSS
 * (see `snippets/pl-header.liquid`); this module only owns the class and the
 * measured bar height.
 *
 * Horizon swaps its scroll container at 990px (`.page-wrapper` on desktop, the
 * document on mobile), so the listener is bound through `@theme/scroll-container`
 * rather than to `window` and is rebound when the breakpoint changes.
 */

import { getScrollEventTarget, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';

/** Class toggled on `<html>`; consumed by the CSS in `snippets/pl-header.liquid`. */
const HIDDEN_CLASS = 'pl-chrome-hidden';

/** Below this scroll offset the chrome is always shown. Matches the Next.js value. */
const TOP_THRESHOLD = 80;

/** Ignore sub-pixel scroll jitter before flipping direction. Matches the Next.js value. */
const DIRECTION_DELTA = 4;

const root = document.documentElement;

/** @type {EventTarget | null} */
let scrollTarget = null;

/** @type {Element | null} */
let observedBar = null;

let lastScrollTop = 0;
let ticking = false;

/**
 * Keeps `--pl-announcement-h` in sync with the real announcement bar height, so the
 * header pill's sticky offset follows whatever padding, font size or extra rows the
 * merchant configures in the theme editor. Falls back to the token in `pl-tokens`.
 */
const resizeObserver = new ResizeObserver(([entry]) => {
  if (!entry) return;

  const blockSize = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
  root.style.setProperty('--pl-announcement-h', `${Math.round(blockSize)}px`);
});

/**
 * Finds the sticky announcement wrapper (the section, not the inner `<aside>`) and
 * observes it. Re-runs on theme editor section loads, which replace the element.
 */
const observeAnnouncement = () => {
  const bar = document.querySelector('.pl-announcement-bar, .announcement-bar');
  const section = bar?.closest('.shopify-section') ?? bar ?? null;

  if (section === observedBar) return;

  if (observedBar) resizeObserver.unobserve(observedBar);
  observedBar = section;

  if (section) {
    resizeObserver.observe(section);
  } else {
    // No announcement bar configured — the pill pins flush to the top.
    root.style.setProperty('--pl-announcement-h', '0px');
  }
};

const update = () => {
  ticking = false;

  const scrollTop = getScrollTop();

  if (scrollTop <= TOP_THRESHOLD) {
    root.classList.remove(HIDDEN_CLASS);
  } else if (scrollTop > lastScrollTop + DIRECTION_DELTA) {
    root.classList.add(HIDDEN_CLASS);
  } else if (scrollTop < lastScrollTop - DIRECTION_DELTA) {
    root.classList.remove(HIDDEN_CLASS);
  }

  lastScrollTop = scrollTop;
};

const handleScroll = () => {
  if (ticking) return;

  ticking = true;
  requestAnimationFrame(update);
};

const bindScroll = () => {
  scrollTarget?.removeEventListener('scroll', handleScroll);
  scrollTarget = getScrollEventTarget();
  scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
  lastScrollTop = getScrollTop();
};

observeAnnouncement();
bindScroll();
update();

scrollContainerMediaQuery.addEventListener('change', bindScroll);
document.addEventListener('shopify:section:load', observeAnnouncement);
document.addEventListener('shopify:section:unload', observeAnnouncement);

// Never leave the chrome hidden while the merchant is editing it.
document.addEventListener('shopify:section:select', () => root.classList.remove(HIDDEN_CLASS));
